import type { ReactNode } from "react";

export type SliderVariant = "continuous" | "discrete";
export type SliderValueLabelDisplay = "off" | "hover" | "auto" | "on";
export type SliderSize = "sm" | "md" | "lg";
export type RangeSliderThumb = "start" | "end";

export interface SliderMark {
  value: number;
  label?: ReactNode;
}

export const DEFAULT_MIN = 0;
export const DEFAULT_MAX = 100;
export const MAX_GENERATED_MARKS = 101;

export const sliderTrackHeights: Record<SliderSize, string> = {
  sm: "0.25rem",
  md: "0.38rem",
  lg: "0.5rem",
};

export const sliderThumbSizes: Record<SliderSize, string> = {
  sm: "1rem",
  md: "1.2rem",
  lg: "1.45rem",
};

export const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

/**
 * 기본 value label 포맷 — 소수 둘째 자리까지 반올림(트레일링 0 제거).
 * continuous(step "any")에서 `14.70000000000001` 같은 부동소수점 noise가 그대로 찍히는 걸 막는다.
 * label은 표시 전용(aria-hidden)이라 실제 값/이벤트 값은 그대로 보존된다. 더 정밀하거나 단위가
 * 필요하면 `formatValue` prop으로 override.
 */
export const formatSliderValue = (value: number) => String(Math.round(value * 100) / 100);

export function normalizeBounds(min: number, max: number) {
  return max > min ? { min, max } : { min: DEFAULT_MIN, max: DEFAULT_MAX };
}

export function percentFromValue(value: number, min: number, max: number) {
  if (max <= min) return 0;
  return ((clamp(value, min, max) - min) / (max - min)) * 100;
}

/**
 * 네이티브 range thumb는 트랙 양 끝에서 thumb 반지름만큼 안쪽으로 들어가 움직인다.
 * 즉 thumb 중심은 0%~100%가 아니라 (thumb/2)~(100% - thumb/2)를 이동한다.
 * value label / tick / 색칠 track 도 같은 궤적을 따라야 thumb와 정확히 정렬된다.
 * percent: 0~100 (값의 정규화 위치)
 */
export function thumbCenter(percent: number) {
  const fraction = percent / 100;
  return `calc(var(--c6-slider-thumb-size) / 2 + ${fraction} * (100% - var(--c6-slider-thumb-size)))`;
}

export function getGeneratedMarks(
  marks: boolean | readonly SliderMark[] | undefined,
  min: number,
  max: number,
  step: number | "any",
) {
  if (Array.isArray(marks)) {
    return marks.filter(
      (mark) => Number.isFinite(mark.value) && mark.value >= min && mark.value <= max,
    );
  }

  if (marks !== true || step === "any" || !Number.isFinite(step) || step <= 0) {
    return [];
  }

  const count = Math.floor((max - min) / step) + 1;
  if (count > MAX_GENERATED_MARKS) {
    return [];
  }

  const generated = Array.from({ length: count }, (_, index) => ({
    value: Math.min(max, min + index * step),
  }));
  const last = generated[generated.length - 1];
  return last && last.value < max ? [...generated, { value: max }] : generated;
}
